import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Button,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from "@mui/icons-material/Add";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaidIcon from "@mui/icons-material/Paid";
import CustomLabel from "../components/CustomLabel";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import CustomTable from "../components/table/CustomTable";
import CustomTextField from "../components/CustomTextField";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import ConfirmDialog from "../components/ConfirmDialog";
import InvoiceItemsEditor, {
  InvoiceItem,
} from "../components/invoices/InvoiceItemsEditor";
import CustomDialog from "../components/CustomDialog";
import CustomButton from "../components/CustomButton";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { COLORS } from "../assets/theme";
import PinAccessDialog from "../components/PinAccessDialog";
import {
  getAllBranches,
  createBranch,
  getAllBusinessTypes,
} from "../services/BranchesService";
import { getAllClients } from "../services/ClientsService";
import { countries } from "../assets/utils/countries";
import { buildClientMap, buildAllAppointments } from "../utils/clientUtils";
import {
  findServiceDuration,
  addMinutesToTime,
  clampEndTime,
} from "../utils/helpers";
import { getAllServices } from "../services/ServicesService";
import {
  updateAppointment,
  deleteAppointment,
  getAppointmentsRevenue,
} from "../services/AppointmentService";
import {
  getInvoiceByAppointmentId,
  updateInvoice,
} from "../services/InvoicesService";
import { getUserId } from "../services/authService";
import CustomSelect from "../components/CustomSelect";
import {
  clearCachedPinAccess,
  getCachedPinAccess,
} from "../utils/pinAccessCache";

const calculateItemTotalPrice = (
  quantity: number,
  unitCost: number,
  discount: number
) => {
  const qty = Number(quantity) || 0;
  const unit = Number(unitCost) || 0;
  const discountPercent = Math.min(100, Math.max(0, Number(discount) || 0));
  const subtotal = qty * unit;
  const discountAmount = (subtotal * discountPercent) / 100;
  return Math.round((subtotal - discountAmount) * 100) / 100;
};

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return undefined;
};

const pickNonEmptyText = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

const parseServiceLabelValue = (label: unknown): number | undefined => {
  const match = String(label ?? "").match(/\d+/);
  if (!match) return undefined;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const resolveStatusFormValue = (
  statusValue?: unknown,
  statusId?: unknown
): "paid" | "unpaid" | "pending" => {
  const normalize = (
    value: unknown
  ): "paid" | "unpaid" | "pending" | undefined => {
    if (typeof value === "number") {
      if (value === 1) return "paid";
      if (value === 2) return "unpaid";
      if (value === 3) return "pending";
      return undefined;
    }

    if (typeof value === "string") {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return normalize(numeric);
      }
      const normalized = value.trim().toLowerCase();
      if (
        normalized === "paid" ||
        normalized === "unpaid" ||
        normalized === "pending"
      ) {
        return normalized;
      }
    }

    return undefined;
  };

  if (statusValue && typeof statusValue === "object") {
    const statusObject = statusValue as Record<string, unknown>;
    return (
      normalize(
        statusObject.value ||
          statusObject.code ||
          statusObject.status ||
          statusObject.name ||
          statusObject.label ||
          statusObject.title
      ) ||
      normalize(
        statusObject.id ||
          statusObject.status_id ||
          statusObject.value ||
          statusObject.code
      ) ||
      normalize(statusId) ||
      "unpaid"
    );
  }

  return normalize(statusValue) || normalize(statusId) || "unpaid";
};

const resolvePaymentMethodFormValue = (
  entry?: any
): "cash" | "card" | "bank transfer" => {
  const candidate =
    typeof entry === "object" && entry !== null
      ? entry.payment_method_id ??
        entry.payment_method?.id ??
        entry.invoice?.payment_method_id ??
        entry.invoice?.payment_method?.id ??
        entry.payment_method ??
        entry.invoice?.payment_method ??
        entry.method ??
        entry.invoice?.method
      : entry;

  const numeric = Number(candidate);
  if (!Number.isNaN(numeric)) {
    if (numeric === 1) return "card";
    if (numeric === 2) return "cash";
    if (numeric === 3) return "bank transfer";
  }

  if (typeof candidate === "string") {
    const normalized = candidate.trim().toLowerCase();
    if (normalized === "cash") return "cash";
    if (normalized === "card") return "card";
    if (
      normalized === "bank transfer" ||
      normalized === "bank_transfer" ||
      normalized === "bank-transfer"
    ) {
      return "bank transfer";
    }
  }

  return "cash";
};

const statusStringToId = (value: "paid" | "unpaid" | "pending"): 1 | 2 | 3 => {
  if (value === "paid") return 1;
  if (value === "unpaid") return 2;
  return 3;
};

const paymentMethodStringToId = (
  value: "cash" | "card" | "bank transfer"
): 1 | 2 | 3 => {
  if (value === "card") return 2;
  if (value === "bank transfer") return 3;
  return 1;
};

const FALLBACK_SERVICE_OPTIONS = [60, 90, 120];
const formatAmountString = (value: number | string | undefined) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric.toFixed(2);
  }
  return "0.00";
};

const formatDateRangePart = (value?: string) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if (trimmed.includes("T")) {
    return trimmed.split("T")[0];
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseRevenueValue = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;
  const numeric =
    typeof value === "string"
      ? Number(value.replace(/,/g, ""))
      : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const parseCountValue = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;
  const numeric =
    typeof value === "string"
      ? Number(value.replace(/,/g, ""))
      : Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : null;
};

const normalizeInvoiceData = (invoiceData: any) => {
  if (!invoiceData) return invoiceData;

  const client =
    invoiceData.client ||
    invoiceData.client_details ||
    invoiceData.invoice?.client ||
    {};

  const normalizedName =
    pickNonEmptyString(
      invoiceData.name,
      invoiceData.fullName,
      client?.name,
      client?.fullName,
      invoiceData.client_name
    ) || "";

  const normalizedMobile =
    pickNonEmptyString(
      invoiceData.mobile,
      client?.mobile,
      client?.phoneNumber,
      client?.phone,
      invoiceData.client_phone
    ) || "";

  const normalizedCreatedAt =
    invoiceData.created_at ||
    invoiceData.invoice_date ||
    invoiceData.invoiceDate ||
    invoiceData.invoice?.created_at ||
    invoiceData.invoice?.invoice_date ||
    invoiceData.date ||
    invoiceData.updated_at ||
    null;

  const normalizedId =
    invoiceData?.id ??
    invoiceData?.invoice?.id ??
    invoiceData?.invoice_id ??
    (Array.isArray(invoiceData?.details) &&
    invoiceData.details.length > 0 &&
    invoiceData.details[0]
      ? invoiceData.details[0].invoice_id
      : undefined);

  const statusIdCandidate =
    invoiceData?.status_id ??
    invoiceData?.status?.id ??
    invoiceData?.status?.status_id ??
    invoiceData?.invoice?.status_id ??
    invoiceData?.invoice?.status?.id ??
    invoiceData?.invoice?.status?.status_id;

  const normalizedStatus = resolveStatusFormValue(
    invoiceData?.status ||
      invoiceData?.invoice?.status ||
      invoiceData?.invoice?.status_name ||
      invoiceData?.invoice?.status_label,
    statusIdCandidate
  );

  const numericStatusId = Number(statusIdCandidate);

  const paymentMethodCandidate =
    invoiceData?.payment_method_id ??
    invoiceData?.payment_method?.id ??
    invoiceData?.invoice?.payment_method_id ??
    invoiceData?.invoice?.payment_method?.id ??
    invoiceData?.method_id ??
    invoiceData?.paymentMethodId ??
    invoiceData?.invoice?.method_id;

  const numericPaymentMethodId = Number(paymentMethodCandidate);

  return {
    ...invoiceData,
    client,
    id: normalizedId ?? invoiceData?.id ?? null,
    name: normalizedName,
    mobile: normalizedMobile,
    created_at: normalizedCreatedAt,
    status: normalizedStatus,
    status_id: Number.isFinite(numericStatusId)
      ? Number(numericStatusId)
      : statusStringToId(normalizedStatus),
    payment_method_id: Number.isFinite(numericPaymentMethodId)
      ? Number(numericPaymentMethodId)
      : paymentMethodStringToId(resolvePaymentMethodFormValue(invoiceData)),
  };
};

const getBranchOrderValue = (branch: any): number | null => {
  const rawOrder =
    branch?.order ??
    branch?.orderNumber ??
    branch?.order_number ??
    branch?.orderNo ??
    branch?.order_no;
  if (rawOrder === "" || rawOrder === undefined || rawOrder === null)
    return null;
  const parsed = Number(rawOrder);
  return Number.isFinite(parsed) ? parsed : null;
};

const sortBranchesByOrder = <T extends { name?: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => {
    const aOrder = getBranchOrderValue(a);
    const bOrder = getBranchOrderValue(b);
    if (aOrder !== null && bOrder !== null) return aOrder - bOrder;
    if (aOrder !== null) return -1;
    if (bOrder !== null) return 1;
    const aName = (a?.name ?? "").toString();
    const bName = (b?.name ?? "").toString();
    return aName.localeCompare(bName);
  });
};

const buildOrderedBranchNames = (items: { name?: string }[]): string[] => {
  const seen = new Set<string>();
  return sortBranchesByOrder(items)
    .map((branch) => (branch?.name ? String(branch.name) : ""))
    .filter((name) => {
      const trimmed = name.trim();
      if (!trimmed || seen.has(trimmed)) return false;
      seen.add(trimmed);
      return true;
    });
};

const formTimeStart = "09:00";

interface Service {
  id: string;
  _id?: string;
  name?: string;
  title?: string;
  duration?: number | null;
  price?: string | number;
  serviceprice?: string | number;
  servicePrice?: string | number;
  price_discount?: string | number;
  service_id?: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const basePath = params.branchId
    ? `/dashboard/${params.branchId}`
    : "/dashboard";
  const [venueId, setVenueId] = React.useState<string | null>(() => {
    return location.state?.venueId || params.branchId || null;
  });
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [activeBranch, setActiveBranch] = React.useState<string | null>(null);
  const [branchList, setBranchList] = React.useState<string[]>([]);
  const [branchClientCounts, setBranchClientCounts] = React.useState<
    Record<string, number>
  >({});
  const [branchStatsLoading, setBranchStatsLoading] = React.useState(false);
  const [projectedRevenue, setProjectedRevenue] = React.useState<number | null>(
    null
  );
  const [paidRevenue, setPaidRevenue] = React.useState<number | null>(null);
  const [appointmentCount, setAppointmentCount] = React.useState<number | null>(
    null
  );
  const [revenueLoading, setRevenueLoading] = React.useState(false);
  const [revenueRefreshKey, setRevenueRefreshKey] = React.useState(0);
  const [revenueVisible, setRevenueVisible] = React.useState(() =>
    getCachedPinAccess()
  );
  const [pinDialogOpen, setPinDialogOpen] = React.useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [serviceOptions, setServiceOptions] = useState<
    { key: string; label: string }[]
  >([]);
  const [editingServiceOptions, setEditingServiceOptions] = useState<
    { key: string; label: string }[]
  >([]);
  const [editingServices, setEditingServices] = useState<Service[]>([]);
  const [invoiceServices, setInvoiceServices] = useState<Service[]>([]);

  const [search, setSearch] = React.useState<string>("");
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const today = `${year}-${month}-${day}`;
    return { start: today, end: today };
  });
  type Branch = {
    name: string;
    image: string;
    sender: string;
    description: string;
    address: string;
    priorCancellationHours: string;
    phone: string;
    _id: string;
    id?: string;
    business_id?: string;
    order?: number | string;
    orderNumber?: number | string;
    order_number?: number | string;
  };
  const [branches, setBranches] = useState<Branch[]>([]);
  const [businessTypes, setBusinessTypes] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedCourt, setSelectedCourt] = useState<string>("all");

  const selectedCourtRef = useRef<string>(selectedCourt);

  const [dialogOpen, setDialogOpen] = useState(false);

  // Invoice editing states
  const [editingInvoice, setEditingInvoice] = useState<any | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [discount, setDiscount] = useState(0);
  const [vat, setVat] = useState(0);
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const patchEditingInvoice = React.useCallback((patch: Partial<any>) => {
    setEditingInvoice((prev: any) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const resolveServiceIdForItem = React.useCallback(
    (item: InvoiceItem): string => {
      if (item.serviceId) {
        return String(item.serviceId);
      }

      const numericService = Number(
        typeof item.service === "number" ? item.service : Number(item.service)
      );
      const servicePool =
        invoiceServices.length > 0 ? invoiceServices : services;
      if (Number.isFinite(numericService) && servicePool.length > 0) {
        const matchedService = servicePool.find((svc: any) => {
          const numericTitle = parseServiceLabelValue(svc.title);
          return (
            numericTitle !== undefined && numericTitle === numericService
          );
        });
        const matchedId =
          matchedService?.id ||
          matchedService?._id ||
          matchedService?.service_id;
        if (matchedId) {
          return String(matchedId);
        }
      }

      return String(
        item.serviceId ||
          item.service ||
          FALLBACK_SERVICE_OPTIONS[0] ||
          `service-${item.id}`
      );
    },
    [invoiceServices, services]
  );

  const fetchInvoiceServices = async (branchId?: string | null) => {
    if (!branchId) {
      setInvoiceServices([]);
      return;
    }
    try {
      const response = await getAllServices(branchId);
      let servicesData = [];
      if (response && response.services) {
        servicesData = response.services;
      } else if (response && response.data && response.data.services) {
        servicesData = response.data.services;
      } else if (response && response.data && Array.isArray(response.data)) {
        servicesData = response.data;
      } else if (Array.isArray(response)) {
        servicesData = response;
      }
      setInvoiceServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (error) {
      console.error("Error fetching invoice services:", error);
      setInvoiceServices([]);
    }
  };

  const openInvoiceEditForId = async (id: string | number) => {
    try {
      setLoadingInvoice(true);
      setInvoiceServices([]);

      // Find the appointment row to get client_id
      const appointmentRow = rows.find((r: any) => String(r.id) === String(id));
      if (!appointmentRow) {
        toast.error("Appointment not found.", { position: "top-right" });
        return;
      }

      const response = await getInvoiceByAppointmentId(String(id));
      const invoiceData = response?.data || response;
      if (!invoiceData) {
        toast.error("No invoice found for this appointment.", {
          position: "top-right",
        });
        return;
      }
      const normalizedInvoice = normalizeInvoiceData(invoiceData);
      const safeNormalized = normalizedInvoice || {};
      const invoiceId =
        safeNormalized?.id ||
        safeNormalized?.invoice_id ||
        invoiceData?.invoice_id ||
        invoiceData?.id ||
        invoiceData?.invoice?.id ||
        id;

      // Ensure client_id and appointment_id are set
      const clientId =
        appointmentRow?.client_id ||
        appointmentRow?.clientId ||
        safeNormalized?.client_id ||
        safeNormalized?.clientId;
      if (!clientId) {
        toast.error("Client ID not found for this appointment.", {
          position: "top-right",
        });
        return;
      }
      const appointmentId = id;

      const normalizedBusinessId =
        safeNormalized.business_id ||
        safeNormalized.invoice?.business_id ||
        safeNormalized.invoice?.businessId ||
        safeNormalized.businessId ||
        appointmentRow?.branchId ||
        appointmentRow?.business_id ||
        appointmentRow?.businessId;

      setEditingInvoice({
        ...safeNormalized,
        id: invoiceId,
        invoice_id: invoiceId,
        client_id: clientId,
        appointment_id: appointmentId,
        business_id: normalizedBusinessId,
      });

      const resolvedDiscount =
        Number(
          safeNormalized?.invoice?.discount ??
            safeNormalized?.discount ??
            invoiceData?.invoice?.discount ??
            invoiceData?.discount ??
            0
        ) || 0;
      const resolvedVat =
        Number(
          safeNormalized?.invoice?.Vat ??
            safeNormalized?.Vat ??
            invoiceData?.invoice?.Vat ??
            invoiceData?.Vat ??
            invoiceData?.vat ??
            0
        ) || 0;
      setDiscount(resolvedDiscount);
      setVat(resolvedVat);

      // Initialize invoice items from whichever details array the API returns
      const detailSource =
        (safeNormalized && safeNormalized.details) ||
        invoiceData.details ||
        safeNormalized.invoice?.details ||
        invoiceData.invoice?.details ||
        safeNormalized.invoice?.data?.details ||
        invoiceData.invoice?.data?.details ||
        safeNormalized.invoice_details ||
        invoiceData.invoice_details;

      if (detailSource && Array.isArray(detailSource)) {
        const items = detailSource.map((detail: any, index: number) => {
          const serviceLabelRaw =
            pickNonEmptyString(
              detail.serviceLabel,
              detail.service_label,
              detail.service_title,
              detail.service_name,
              detail.title
            ) || "";
          const numericFromLabel = parseServiceLabelValue(serviceLabelRaw);
          const detailServiceId =
            detail.service_id ??
            detail.serviceId ??
            detail.serviceID ??
            (detail.service && typeof detail.service === "object"
              ? detail.service.id ||
                detail.service._id ||
                detail.service.service_id
              : undefined);
          const detailServiceRaw =
            typeof detail.service === "string" ? detail.service.trim() : "";
          const detailServiceIsId =
            detailServiceRaw && Number.isNaN(Number(detailServiceRaw));
          const normalizedServiceId =
            detailServiceId ?? (detailServiceIsId ? detailServiceRaw : undefined);
          const normalizedServiceIdText =
            normalizedServiceId !== undefined && normalizedServiceId !== null
              ? String(normalizedServiceId)
              : undefined;
          const derivedServiceValue =
            (normalizedServiceIdText
              ? normalizedServiceIdText
              : undefined) ??
            (numericFromLabel && numericFromLabel > 0
              ? numericFromLabel
              : undefined) ??
            (!Number.isNaN(Number(detail.service))
              ? Number(detail.service)
              : undefined) ??
            (!Number.isNaN(Number(detail.service_id))
              ? Number(detail.service_id)
              : undefined) ??
            FALLBACK_SERVICE_OPTIONS[0];
          const quantityValue = Number(detail.quantity) || 1;
          const unitCostValue =
            Number(detail.amount ?? detail.unit_cost ?? detail.price) || 0;
          const discountValue = Number(detail.discount) || 0;
          const fallbackServiceLabel =
            serviceLabelRaw || String(derivedServiceValue);

          return {
            id: index + 1,
            service: derivedServiceValue,
            serviceId: normalizedServiceIdText,
            serviceLabel: fallbackServiceLabel,
            quantity: quantityValue,
            unitCost: unitCostValue,
            discount: discountValue,
            totalPrice:
              Number(detail.net_amount) ||
              calculateItemTotalPrice(
                quantityValue,
                unitCostValue,
                discountValue
              ),
          } as InvoiceItem;
        });
        setInvoiceItems(items);
      } else {
        // If the backend did not send details, keep existing items to avoid dropping the UI
        setInvoiceItems((prev) =>
          Array.isArray(prev) && prev.length > 0
            ? prev
            : [
                {
                  id: 1,
                  service: 60,
                  serviceLabel: "60",
                  quantity: 1,
                  unitCost: 40,
                  discount: 0,
                  totalPrice: calculateItemTotalPrice(1, 40, 0),
                },
              ]
        );
      }

      const appointmentBranchId =
        appointmentRow?.branchId ||
        appointmentRow?.business_id ||
        appointmentRow?.businessId ||
        normalizedBusinessId;

      setInvoiceEditOpen(true);
      await fetchInvoiceServices(appointmentBranchId);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      toast.error("Failed to load invoice.", { position: "top-right" });
    } finally {
      setLoadingInvoice(false);
    }
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        if (!venueId) {
          console.error("No venue ID available for services");
          setServices([]);
          return;
        }
        let targetBranch;
        const branchList =
          Array.isArray(branches) && branches.length ? branches : branches;
        if (selectedCourt === "all" || !selectedCourt) {
          targetBranch = branchList?.[0];
        } else {
          targetBranch =
            (branchList || []).find(
              (b) => String(b.id) === String(selectedCourt)
            ) || branchList?.[0];
        }

        const businessId = targetBranch?.business_id;
        if (!businessId) {
          console.error("No valid business ID found for services");
          setServices([]);
          return;
        }

        const response = await getAllServices(businessId);
        console.log("Services fetched for business:", businessId, response);
        let servicesData = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }

        setServices(Array.isArray(servicesData) ? servicesData : []);

        const options = (Array.isArray(servicesData) ? servicesData : []).map(
          (service) => ({
            key: service.id || service._id,
            label: String(service.title || service.name || ""),
          })
        );
        setServiceOptions(options);
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices([]);
      } finally {
      }
    };

    fetchServices();
  }, [venueId, selectedCourt, branches]);

  useEffect(() => {
    const fetchEditingServices = async () => {
      if (!editOpen || !editingRow) return;
      const businessId =
        editingRow.business_id ||
        branches.find((b) => b.name === editingRow.branch)?._id ||
        branches.find((b) => b.name === editingRow.branch)?.id;
      if (!businessId) return;
      try {
        const response = await getAllServices(businessId);
        let servicesData = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }
        const options = (Array.isArray(servicesData) ? servicesData : []).map(
          (service) => ({
            key: service.id || service._id,
            label: String(service.title || service.name || ""),
          })
        );
        setEditingServiceOptions(options);
        const svcArray = Array.isArray(servicesData) ? servicesData : [];
        setEditingServices(svcArray);
        // if editingRow has service_id, ensure the price is set accordingly
        if (editingRow && editingRow.service_id) {
          const matchedById = svcArray.find(
            (s: any) =>
              String(s.id || s._id || s.service_id || "") ===
              String(editingRow.service_id)
          );
          if (matchedById) {
            const price =
              matchedById.price ??
              matchedById.serviceprice ??
              matchedById.servicePrice ??
              matchedById.price_discount ??
              "";
            // only update if changed to avoid re-triggering this effect
            if (String(editingRow.servicePrice ?? "") !== String(price ?? "")) {
              setEditingRow((prev: any) => ({
                ...(prev || {}),
                servicePrice: String(price ?? ""),
              }));
            }
          }
        }
        // If the appointment doesn't have a service_id, attempt to guess it from the service price/title
        if (editingRow && !editingRow.service_id) {
          try {
            const appointmentPrice =
              editingRow.servicePrice ||
              editingRow.serviceprice ||
              editingRow.servicePrice ||
              editingRow.serviceprice;
            if (appointmentPrice) {
              const appointmentPriceStr = String(appointmentPrice).trim();
              const matchedService = (
                Array.isArray(servicesData) ? servicesData : []
              ).find(
                (s: any) =>
                  String(
                    s.price || s.serviceprice || s.servicePrice || ""
                  ).trim() === appointmentPriceStr ||
                  String(s.title || s.name || "").trim() === appointmentPriceStr
              );
              if (matchedService) {
                const newServiceId =
                  matchedService.id ||
                  matchedService._id ||
                  matchedService.service_id;
                if (
                  String(editingRow.service_id ?? "") !==
                  String(newServiceId ?? "")
                ) {
                  setEditingRow((prev: any) => ({
                    ...(prev || {}),
                    service_id: newServiceId,
                  }));
                }
              }
            }
          } catch (err) {
            // ignore matching errors
          }
        }
      } catch (error) {
        console.error("Error fetching editing services:", error);
        setEditingServiceOptions([]);
      }
    };
    fetchEditingServices();
  }, [editOpen, editingRow?.id, branches]);

  const fetchBranches = async () => {
    setBranchStatsLoading(true);
    try {
      if (!venueId) {
        console.error("No venue ID available");
        return;
      }
      const response = await getAllBranches(venueId);

      const businessesSource = (() => {
        if (Array.isArray(response)) return response;
        if (Array.isArray((response as any)?.businesses))
          return (response as any).businesses;
        if (Array.isArray((response as any)?.data?.businesses))
          return (response as any).data.businesses;
        if (Array.isArray((response as any)?.data))
          return (response as any).data;
        if (Array.isArray((response as any)?.venue?.businesses))
          return (response as any).venue.businesses;
        return [];
      })();

      if (Array.isArray(businessesSource)) {
        // Show all branches from the API; avoid filtering them out by date.
        const filteredBusinesses = businessesSource;

        const sortedBranches = sortBranchesByOrder(filteredBusinesses);

        setBranches(sortedBranches);
        setBranchList(buildOrderedBranchNames(sortedBranches));

        const { clientMap, clientCounts, clientPhones } = await buildClientMap(
          filteredBusinesses
        );
        console.log("Client Map:", clientMap);

        const countsByBranchName = filteredBusinesses.reduce(
          (acc: Record<string, number>, branch: any) => {
            const branchName = String(branch.name);
            const branchKey =
              branch.business_id || branch.id || branch._id || branchName;
            const normalizedKey = branchKey ? String(branchKey) : branchName;
            if (
              normalizedKey &&
              clientCounts[normalizedKey] !== undefined &&
              clientCounts[normalizedKey] !== null
            ) {
              acc[branchName] = clientCounts[normalizedKey];
            } else {
              acc[branchName] = acc[branchName] ?? 0;
            }
            return acc;
          },
          {}
        );
        setBranchClientCounts(countsByBranchName);

        const allAppointments = buildAllAppointments(
          filteredBusinesses,
          clientMap,
          clientPhones
        );
        console.log("All Appointments:", allAppointments);

        const parseTimestamp = (value: any) => {
          if (!value) return 0;
          const date = new Date(value);
          const time = date.getTime();
          return Number.isNaN(time) ? 0 : time;
        };

        const sortedAppointments = [...allAppointments].sort((a, b) => {
          const bTime = parseTimestamp(b.createdAt || b.created_at || b.date);
          const aTime = parseTimestamp(a.createdAt || a.created_at || a.date);
          return bTime - aTime;
        });

        setRows(sortedAppointments);
      }
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    } finally {
      setBranchStatsLoading(false);
    }
  };
  const fetchBusinessTypes = async () => {
    try {
      const response = await getAllBusinessTypes();
      console.log("Business types response:", response);
      if (Array.isArray(response)) {
        const normalized = response
          .map((type: any) => {
            const id =
              type?.id ??
              type?.type_id ??
              type?._id ??
              type?.value ??
              type?.uuid;
            const name =
              type?.name ??
              type?.value ??
              type?.type_name ??
              type?.label ??
              type?.title ??
              type?.description;

            if (!id || !name) return null;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];

        const unique = Array.from(
          new Map(normalized.map((type) => [type.id, type])).values()
        ).sort((a, b) => a.name.localeCompare(b.name));

        setBusinessTypes(unique);
      } else {
        setBusinessTypes([]);
      }
    } catch (error) {
      console.error("Failed to fetch business types:", error);
      setBusinessTypes([]);
    }
  };

  React.useEffect(() => {
    fetchBranches();
    fetchBusinessTypes();
  }, [dateRange]);

  React.useEffect(() => {
    if (venueId) {
      fetchBranches();
    }
  }, [venueId, dateRange]);

  React.useEffect(() => {
    const [startValue, endValue] = Array.isArray(dateRange)
      ? dateRange
      : [dateRange?.start, dateRange?.end];
    const startDate = formatDateRangePart(startValue);
    const endDate = formatDateRangePart(endValue);

    if (!venueId || !startDate || !endDate) {
      setProjectedRevenue(null);
      setPaidRevenue(null);
      setAppointmentCount(null);
      setRevenueLoading(false);
      return;
    }

    let isMounted = true;
    setProjectedRevenue(null);
    setPaidRevenue(null);
    setAppointmentCount(null);
    setRevenueLoading(true);

    const fetchRevenue = async () => {
      try {
        const response = await getAppointmentsRevenue(
          venueId,
          startDate,
          endDate
        );
        if (!isMounted) return;
        const payload = response?.data ?? response;
        const paid = parseRevenueValue(
          payload?.paid_revenue ?? payload?.paidRevenue ?? payload?.paid
        );
        const projected = parseRevenueValue(
          payload?.projected_revenue ??
            payload?.projectedRevenue ??
            payload?.projected
        );
        const count = parseCountValue(
          payload?.appointment_count ??
            payload?.appointmentCount ??
            payload?.count
        );
        setPaidRevenue(paid);
        setProjectedRevenue(projected);
        setAppointmentCount(count);
      } catch (error) {
        if (!isMounted) return;
        setProjectedRevenue(null);
        setPaidRevenue(null);
        setAppointmentCount(null);
      } finally {
        if (isMounted) {
          setRevenueLoading(false);
        }
      }
    };

    fetchRevenue();

    return () => {
      isMounted = false;
    };
  }, [dateRange, venueId, revenueRefreshKey]);

  const formFields: Field[] = [
    {
      name: "business_id",
      label: "Branch",
      disabled: true,
      type: "dropdown",
      options: branches
        .filter((b) => b && b.name)
        .map((branch) => ({
          label: String(branch.name),
          value: String(branch._id || branch.id || branch.name),
        })),
    },
    { name: "date", label: "Date", type: "date" },

    {
      name: "service_id",
      label: "Service",
      type: "dropdown",
      customRender: (value, onChange, helpers) => {
        const opts = editingServiceOptions.map((s) => ({
          label: s.label,
          value: s.key,
        }));
        return (
          <>
            <CustomLabel text="Service" fontWeight={500} fontSize={14} />

            <CustomSelect
              label="Service"
              value={value || ""}
              options={opts}
              onChange={(v: any) => {
                const str = String(v || "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("service_id", str);
                  try {
                    const svc = editingServices.find(
                      (s) =>
                        String(s.id || s._id || s.service_id || "") ===
                        String(str)
                    );
                    if (svc) {
                      const price =
                        svc.price ??
                        svc.serviceprice ??
                        svc.servicePrice ??
                        svc.price_discount ??
                        "";
                      helpers.setFieldValue(
                        "servicePrice",
                        String(price ?? "")
                      );
                    }
                  } catch (err) {}
                  const currentStart =
                    helpers.values && helpers.values["time_start"]
                      ? helpers.values["time_start"]
                      : formTimeStart;
                  try {
                    const dur = findServiceDuration(str, editingServices);
                    if (dur && currentStart) {
                      const newEndDesired = addMinutesToTime(
                        String(currentStart),
                        dur
                      );
                      const clamped = clampEndTime(
                        String(currentStart),
                        newEndDesired,
                        120
                      );
                      helpers.setFieldValue("time_end", clamped);
                    }
                  } catch (e) {}
                }
              }}
              fullWidth
            />
          </>
        );
      },
    },

    {
      name: "servicePrice",
      label: "Service Price",
      type: "text",
      disabled: true,
    },
    { name: "time_start", label: "Start Time", disabled: true },
    { name: "time_end", label: "End Time", disabled: true },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: ["Done", "Pending", "Cancelled"],
    },
    { name: "notes", label: "Notes" },
  ];

  const handleDialogSubmit = async (values: Record<string, any>) => {
    try {
      // Validation for required fields
      const requiredFields = [
        { key: "name", label: "Branch Name" },
        { key: "short_name", label: "Email sender" },
        { key: "type_id", label: "Business Type" },
        { key: "countryCode", label: "Country Code" },
        { key: "phoneNumber", label: "Phone Number" },
      ];

      const missingFields: string[] = [];
      for (const field of requiredFields) {
        const value = values[field.key];
        if (!value || String(value).trim() === "") {
          missingFields.push(field.label);
        }
      }

      if (missingFields.length > 0) {
        toast.error(
          `Please fill in the following required fields: ${missingFields.join(
            ", "
          )}`,
          {
            position: "top-right",
          }
        );
        return;
      }

      const fieldMapping: Record<string, string> = {
        name: "name",
        short_name: "short_name",
        description: "description",
        address: "address",
        fn_number: "fn_number",
        phoneNumber: "business_phone",
        countryCode: "countryCode",
        prior_cancel_hours: "prior_cancel_hours",
        latitude: "latitude",
        longitude: "longitude",
        image: "image",
        orderNumber: "order",
      };

      let businessPhone = "";
      if (values.countryCode && values.phoneNumber) {
        const countryCode = values.countryCode.replace(/\s/g, "");
        businessPhone = `${countryCode}${values.phoneNumber}`;
      } else if (values.phoneNumber) {
        businessPhone = values.phoneNumber;
      }

      const payload: Record<string, any> = {};
      if (venueId) payload.venue_id = venueId;

      if (values.type_id) {
        const selectedType = businessTypes.find(
          (type) => String(type.id) === String(values.type_id)
        );
        const typeIdToSend = selectedType?.id ?? String(values.type_id);
        const typeNameToSend = selectedType?.name ?? String(values.type_id);

        payload.type_id = typeIdToSend;
        payload.type_name = typeNameToSend;
      }

      const parsedOrderNumber =
        values.orderNumber !== undefined && values.orderNumber !== ""
          ? parseInt(String(values.orderNumber), 10)
          : undefined;
      if (parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)) {
        payload.orderNumber = parsedOrderNumber;
        payload.order = parsedOrderNumber;
      }

      Object.keys(values).forEach((key) => {
        const value = values[key];
        const backendFieldName = fieldMapping[key] || key;

        if (key === "image") {
          if (value && value instanceof File) {
            payload.image = value;
          }
        } else if (
          key === "phoneNumber" ||
          key === "countryCode" ||
          key === "type_id" ||
          key === "orderNumber"
        ) {
          return;
        } else if (value !== undefined && value !== null && value !== "") {
          payload[backendFieldName] = value;
        }
      });

      if (businessPhone) {
        payload.business_phone = businessPhone;
      }

      const isAddressEmpty = !String(values.address ?? "").trim();
      if (isAddressEmpty) {
        payload.address = "";
        payload.latitude = null;
        payload.longitude = null;
      } else {
        if (!String(values.latitude ?? "").trim()) {
          payload.latitude = null;
        }
        if (!String(values.longitude ?? "").trim()) {
          payload.longitude = null;
        }
      }

      const hasFile = payload.image instanceof File;
      if (hasFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined) return;
          if (value instanceof File) {
            formData.append(key, value);
          } else if (value === null) {
            formData.append(key, "");
          } else {
            formData.append(key, String(value));
          }
        });
        await createBranch(formData);
      } else {
        await createBranch(payload);
      }
      toast.success("Branch created successfully!", { position: "top-right" });
      fetchBranches();
      setDialogOpen(false);
    } catch (error) {
      console.error("Failed to create branch:", error);
      toast.error("Failed to create branch.", { position: "top-right" });
    }
  };
  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
    onPdf: {
      handler: async (id: number | string) => {
        await openInvoiceEditForId(id);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (branches.length > 0) {
      setBranchList(buildOrderedBranchNames(branches));
    } else {
      setBranchList([]);
    }
  }, [branches]);

  const fields: Field[] = [
    { type: "text", name: "name", label: "Branch Name", required: true },
    {
      type: "number",
      name: "orderNumber",
      label: "Order Number",
      required: true,
    },
    { type: "text", name: "short_name", label: "Email sender", required: true },
    {
      name: "type_id",
      label: "Business Type",
      type: "dropdown",
      options: businessTypes.map((type) => ({
        label: String(type.name),
        value: type.id,
      })),
      required: true,
    },
    {
      name: "address",
      label: "Address",
      colSpan: 2,
      customRender: (
        value: any,
        onChange: (v: any) => void,
        helpers?: {
          setFieldValue: (name: string, value: any) => void;
          values?: Record<string, any>;
        },
        error?: string
      ) => {
        return (
          <Box>
            <AddressAutocomplete
              value={value}
              onChange={onChange}
              helpers={helpers}
              error={error}
            />
          </Box>
        );
      },
    },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => `+${c.phone}`),
      required: true,
    },
    {
      type: "text",
      name: "phoneNumber",
      label: "Phone Number",
      required: true,
    },
    { type: "text", name: "description", label: "Description" },

    {
      type: "text",
      name: "prior_cancel_hours",
      label: "Prior Cancellation Hours",
    },
    // { type: "file", name: "image", label: "Branch Image" },
  ];

  const exportCsv = React.useCallback(() => {
    if (!rows || rows.length === 0) return;
    const keys = [
      "clientName",
      "bookingSource",
      "createdBy",
      "phoneNumber",
      // "priority",
      "date",
      "branch",
      "status",
      "startTime",
      "endTime",
      "servicePrice",
      "promoCodeUsed",
      "notes",
    ];
    const header = keys.join(",");
    const lines = rows.map((r: any) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  const revenueRangeText = (() => {
    if (!dateRange) return "Date range: select in table";
    const [startValue, endValue] = Array.isArray(dateRange)
      ? dateRange
      : [dateRange.start, dateRange.end];
    const startLabel = formatDateRangePart(startValue);
    const endLabel = formatDateRangePart(endValue);

    if (startLabel && endLabel) {
      return `Date range: ${startLabel} - ${endLabel}`;
    }
    if (startLabel) {
      return `Date range: ${startLabel}`;
    }
    if (endLabel) {
      return `Date range: ${endLabel}`;
    }
    return "Date range: select in table";
  })();

  const projectedRevenueDisplay = revenueLoading
    ? "Loading..."
    : projectedRevenue !== null
    ? formatAmountString(projectedRevenue)
    : "TBD";
  const paidRevenueDisplay = revenueLoading
    ? "Loading..."
    : paidRevenue !== null
    ? formatAmountString(paidRevenue)
    : "TBD";
  const appointmentCountDisplay = revenueLoading
    ? "Loading..."
    : appointmentCount !== null
    ? String(appointmentCount)
    : "TBD";
  const maskedProjectedRevenueDisplay = revenueVisible
    ? projectedRevenueDisplay
    : "Hidden";
  const maskedPaidRevenueDisplay = revenueVisible ? paidRevenueDisplay : "Hidden";
  const maskedAppointmentCountDisplay = revenueVisible
    ? appointmentCountDisplay
    : "Hidden";
  const invoiceNumberLabel =
    pickNonEmptyText(
      editingInvoice?.invoice_number,
      editingInvoice?.Invoice_number,
      editingInvoice?.invoice?.invoice_number,
      editingInvoice?.invoice?.Invoice_number,
      editingInvoice?.invoice_id,
      editingInvoice?.id
    ) || "";
  const invoiceClientName =
    pickNonEmptyString(
      editingInvoice?.client?.name,
      editingInvoice?.client?.fullName,
      editingInvoice?.client?.full_name,
      editingInvoice?.name,
      editingInvoice?.client_name
    ) || "";
  const invoiceClientEmail =
    pickNonEmptyString(
      editingInvoice?.client?.email,
      editingInvoice?.email,
      editingInvoice?.client_email
    ) || "";
  const invoiceClientPhone =
    pickNonEmptyString(
      editingInvoice?.client?.mobile,
      editingInvoice?.client?.phoneNumber,
      editingInvoice?.client?.phone,
      editingInvoice?.mobile,
      editingInvoice?.client_phone
    ) || "";
  const invoiceDateValue = formatDateRangePart(
    editingInvoice?.invoice_date ||
      editingInvoice?.invoice?.invoice_date ||
      editingInvoice?.created_at ||
      editingInvoice?.invoice?.created_at
  );

  const summaryCardSx = {
    borderRadius: 4,
    boxShadow: 2,
    minHeight: { xs: 120, sm: 140 },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: COLORS.MainColor,
    border: "2px solid rgba(255, 255, 255, 0.2)",
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: 4,
    },
    "&:focus-visible": {
      outline: "2px solid rgba(255, 255, 255, 0.9)",
      outlineOffset: 2,
    },
  };

  const summaryCardContentSx = {
    textAlign: "center",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
  };
  const summaryCardPrimaryTextColor = COLORS.WhiteText;
  const summaryCardSecondaryTextColor = "rgba(255, 255, 255, 0.75)";
  const handleCardKeyDown = (
    event: React.KeyboardEvent,
    action: () => void
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };
  const navigateToAppointments = () => {
    navigate(`${basePath}/appointment`);
  };
  const navigateToInvoices = () => {
    navigate(`${basePath}/finance/invoices`);
  };
  const handleOpenPinDialog = () => {
    if (revenueVisible || pinDialogOpen) return;
    setPinDialogOpen(true);
  };
  const handleClosePinDialog = () => {
    setPinDialogOpen(false);
  };
  const handlePinVerified = () => {
    setRevenueVisible(true);
    setPinDialogOpen(false);
  };
  const handleHideRevenue = () => {
    clearCachedPinAccess();
    setRevenueVisible(false);
    setPinDialogOpen(false);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100vw", overflowX: "hidden", pt: 3 }}>
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* <Box
          sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" }, mb: 2 }}
        >
          Overview
        </Box> */}
        <Box
          sx={{
            display: "grid",
            gap: { xs: 2, sm: 3 },
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            alignItems: "stretch",
            justifyContent: "center",
          }}
        >
          <Card
            sx={summaryCardSx}
            onClick={navigateToAppointments}
            onKeyDown={(event) => handleCardKeyDown(event, navigateToAppointments)}
            role="button"
            tabIndex={0}
            aria-label="Go to appointments"
          >
            <CardContent sx={summaryCardContentSx}>
              <EventAvailableIcon
                sx={{
                  fontSize: { xs: 32, sm: 30 },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text="Total Appointments"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.95rem", sm: "0.85rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={maskedAppointmentCountDisplay}
                fontWeight={700}
                center
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.4rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={revenueRangeText}
                fontWeight={500}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.7rem" },
                  color: summaryCardSecondaryTextColor,
                }}
              />
            </CardContent>
          </Card>

          <Card
            sx={summaryCardSx}
            onClick={navigateToInvoices}
            onKeyDown={(event) => handleCardKeyDown(event, navigateToInvoices)}
            role="button"
            tabIndex={0}
            aria-label="Go to invoices"
          >
            <CardContent sx={summaryCardContentSx}>
              <TrendingUpIcon
                sx={{
                  fontSize: { xs: 32, sm: 30 },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text="Projected Revenue"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.95rem", sm: "0.85rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={maskedProjectedRevenueDisplay}
                fontWeight={700}
                center
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.4rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={revenueRangeText}
                fontWeight={500}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.7rem" },
                  color: summaryCardSecondaryTextColor,
                }}
              />
            </CardContent>
          </Card>

          <Card
            sx={summaryCardSx}
            onClick={navigateToInvoices}
            onKeyDown={(event) => handleCardKeyDown(event, navigateToInvoices)}
            role="button"
            tabIndex={0}
            aria-label="Go to invoices"
          >
            <CardContent sx={summaryCardContentSx}>
              <PaidIcon
                sx={{
                  fontSize: { xs: 32, sm: 30 },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text="Paid Revenue"
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.95rem", sm: "0.85rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={maskedPaidRevenueDisplay}
                fontWeight={700}
                center
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.4rem" },
                  color: summaryCardPrimaryTextColor,
                }}
              />
              <CustomLabel
                text={revenueRangeText}
                fontWeight={500}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.7rem" },
                  color: summaryCardSecondaryTextColor,
                }}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
      <Divider
        sx={{
          my: { xs: 2, sm: 3 },
          borderColor: COLORS.BorderColor,
          borderBottomWidth: 2,
        }}
      />
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box
            sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Branches
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              variant="outlined"
              onClick={
                revenueVisible ? handleHideRevenue : handleOpenPinDialog
              }
              disabled={pinDialogOpen}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {revenueVisible ? "Hide finance" : "Show finance"}
            </Button>
            {/* <CustomSelect
              label="Select Branch"
              value={selectedCourt}
              options={[
                { label: "All Branches", value: "all" },
                ...branches
                  .filter((b) => b && b.name)
                  .map((b) => ({
                    label: String(b.name),
                    value: String(b.id || b._id),
                  })),
              ]}
              onChange={(v: any) => setSelectedCourt(String(v || "all"))}
              sx={{ minWidth: 200 }}
            /> */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Add branch
            </Button>
          </Box>
        </Box>

        {branchStatsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: { xs: 2, sm: 3 },
              gridTemplateColumns:
                branchList.length === 1
                  ? {
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(1, 1fr)",
                      md: "repeat(1, 1fr)",
                    }
                  : branchList.length === 2
                  ? {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(2, 1fr)",
                    }
                  : branchList.length <= 6
                  ? {
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                    }
                  : branchList.length <= 12
                  ? {
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(4, 1fr)",
                    }
                  : {
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(auto-fit, minmax(180px, 1fr))",
                    },
              alignItems: "stretch",
              justifyContent: "center",
            }}
          >
            {branchList.map((branchName) => {
              const branchDetails = branches.find((b) => b.name === branchName);
              const branchRows = rows.filter(
                (r: any) => r.branch === branchName
              );
              const clientIds = branchRows
                .map((r: any) => {
                  const id = r.client_id ?? r.clientId ?? "";
                  return id ? String(id).trim().toLowerCase() : null;
                })
                .filter(Boolean);
              const branchNameKey = String(branchName);
              const totalClients =
                branchClientCounts[branchNameKey] !== undefined
                  ? branchClientCounts[branchNameKey]
                  : new Set(clientIds).size;
              const todaysAppointments = branchRows.filter((r: any) => {
                const d = new Date(r.date);
                return d >= todayStart && d <= todayEnd;
              }).length;
              const totalVenues = 1;

              return (
                <Box
                  key={branchName}
                  sx={{ flex: 1, cursor: "pointer" }}
                  onClick={() =>
                    setActiveBranch((prev) =>
                      prev === branchName ? null : branchName
                    )
                  }
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: activeBranch === branchName ? 6 : 2,
                      minHeight: { xs: 140, sm: 160 },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: COLORS.WhiteBackground,
                      border: `2px solid ${
                        activeBranch === branchName
                          ? COLORS.MainColor
                          : COLORS.BorderColor
                      }`,
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", width: "100%" }}>
                      <CustomLabel
                        text={branchName}
                        fontWeight={550}
                        fontSize="1rem"
                        center
                        sx={{
                          mb: 2,
                          fontSize: { xs: "1.05rem", sm: "1rem" },
                          color: COLORS.BlackText,
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <Box sx={{ textAlign: "center", minWidth: 80 }}>
                          <PeopleIcon
                            sx={{
                              fontSize: { xs: 28, sm: 26 },
                              color: COLORS.MainColor,
                              mb: 0.5,
                            }}
                          />
                          <CustomLabel
                            text="Total Clients"
                            fontWeight={600}
                            sx={{
                              fontSize: { xs: "0.85rem", sm: "0.70rem" },
                            }}
                          />
                          <CustomLabel
                            text={String(totalClients)}
                            fontWeight={700}
                            center
                            sx={{
                              fontSize: { xs: "1rem", sm: "0.70rem" },
                              color: COLORS.BlackText,
                            }}
                          />
                        </Box>

                        <Box sx={{ textAlign: "center", minWidth: 90 }}>
                          <EditIcon
                            sx={{
                              fontSize: { xs: 28, sm: 26 },
                              color: COLORS.MainColor,
                              mb: 0.5,
                            }}
                          />
                          <CustomLabel
                            text="Today's Appointments"
                            fontWeight={600}
                            sx={{
                              fontSize: { xs: "0.85rem", sm: "0.70rem" },
                            }}
                          />
                          <CustomLabel
                            text={String(todaysAppointments)}
                            fontWeight={700}
                            center
                            sx={{
                              fontSize: { xs: "1rem", sm: "0.70rem" },
                              color: COLORS.BlackText,
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      <Divider
        sx={{
          my: { xs: 2, sm: 3 },
          borderColor: COLORS.BorderColor,
          borderBottomWidth: 2,
        }}
      />
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
          selectConfig={{
            options: [
              { label: "Pending", value: "pending" },
              { label: "Done", value: "done" },
              { label: "Cancelled", value: "cancelled" },
            ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select action",
            size: "medium",
            label: "Status ",
            searchable: false,
            clearable: true,

            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              label: "Export CSV",
              onClick: () => Promise.resolve(exportCsv()),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search, branch: activeBranch }}
          clientSidePagination={true}
          fields={[
            { key: "clientName", header: "Client Name" },
            { key: "notes", header: "Notes" },
            { key: "phoneNumber", header: "Phone Number" },
            // { key: "priority", header: "Priority" },
            { key: "date", header: "Date" },
            { key: "branch", header: "Branch" },
            { key: "status", header: "Status" },
            { key: "startTime", header: "Start Time" },
            { key: "endTime", header: "End Time" },
            { key: "servicePrice", header: "Service Price" },
            { key: "promoCodeUsed", header: "Promo Code Used" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";
            const branchFilter = (params && params.branch) ?? activeBranch;
            const statusFilter = Array.isArray(selectValue)
              ? selectValue.map(String)
              : selectValue
              ? [String(selectValue)]
              : [];

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const parseDateValue = (
              raw?: string,
              endOfDay: boolean = false
            ): Date | null => {
              if (!raw) return null;
              const value = String(raw).trim();
              if (!value) return null;

              const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
              const dmyMatch = value.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})/);

              let year: number | null = null;
              let month: number | null = null;
              let day: number | null = null;

              if (isoMatch) {
                year = Number(isoMatch[1]);
                month = Number(isoMatch[2]);
                day = Number(isoMatch[3]);
              } else if (dmyMatch) {
                day = Number(dmyMatch[1]);
                month = Number(dmyMatch[2]);
                year = Number(dmyMatch[3]);
              }

              let parsedDate: Date | null = null;

              if (
                year !== null &&
                month !== null &&
                day !== null &&
                Number.isFinite(year) &&
                Number.isFinite(month) &&
                Number.isFinite(day)
              ) {
                parsedDate = new Date(year, month - 1, day);
              } else {
                const parsed = new Date(value);
                if (!Number.isNaN(parsed.getTime())) {
                  parsedDate = new Date(
                    parsed.getFullYear(),
                    parsed.getMonth(),
                    parsed.getDate()
                  );
                }
              }

              if (!parsedDate) return null;

              parsedDate.setHours(
                endOfDay ? 23 : 0,
                endOfDay ? 59 : 0,
                endOfDay ? 59 : 0,
                endOfDay ? 999 : 0
              );
              return parsedDate;
            };

            const startDateFilter = dateRange
              ? Array.isArray(dateRange)
                ? parseDateValue((dateRange as any)[0])
                : parseDateValue((dateRange as any).start)
              : null;
            let endDateFilter = dateRange
              ? Array.isArray(dateRange)
                ? parseDateValue((dateRange as any)[1], true)
                : parseDateValue((dateRange as any).end, true)
              : null;

            if (startDateFilter && !endDateFilter) {
              endDateFilter = new Date(startDateFilter);
              endDateFilter.setHours(23, 59, 59, 999);
            }

            const filtered = rows.filter((r: any) => {
              if (branchFilter) {
                if (r.branch !== branchFilter) return false;
              }
              if (statusFilter.length > 0) {
                const statusMatch = statusFilter.some((sv) =>
                  String(r.status)
                    .toLowerCase()
                  .includes(String(sv).toLowerCase())
                );
                if (!statusMatch) return false;
              }

              if (startDateFilter || endDateFilter) {
                const rowDate = parseDateValue(
                  r.start_date ||
                    r.startDate ||
                    r.date ||
                    r.createdAt ||
                    r.created_at
                );

                if (rowDate && r.startTime) {
                  const [hours, minutes] = String(r.startTime)
                    .split(":")
                    .map(Number);
                  if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                    rowDate.setHours(hours, minutes, 0, 0);
                  }
                }

                if (rowDate) {
                  if (startDateFilter && rowDate < startDateFilter) return false;
                  if (endDateFilter && rowDate > endDateFilter) return false;
                }
              }

              if (!normalizedQuery) return true;

              const haystack = [
                r.clientName,
                r.bookingSource,
                r.createdBy,
                r.phoneNumber,
                // r.priority,
                r.status,
                r.date,
                r.branch,
                r.startTime,
                r.endTime,
                r.servicePrice,
                r.promoCodeUsed,
                r.notes,
              ]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered.sort((a, b) => {
              const aDate = new Date(a.date);
              const bDate = new Date(b.date);
              if (aDate.getTime() !== bDate.getTime()) {
                return aDate.getTime() - bDate.getTime();
              }
              const aStart = a.startTime || "00:00";
              const bStart = b.startTime || "00:00";
              return aStart.localeCompare(bStart);
            });
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={
            editingRow
              ? `Edit Appointment: ${
                  editingRow.name || editingRow.clientName || ""
                }`
              : "Edit"
          }
          tabs={[{ label: "Appointment", fields: [...formFields] }]}
          initialValues={
            editingRow
              ? {
                  ...Object.keys(editingRow).reduce((acc: any, key) => {
                    const val = editingRow[key];
                    acc[key] =
                      val === undefined || val === null ? "" : String(val);
                    return acc;
                  }, {}),
                  business_id:
                    editingRow.business_id ||
                    branches.find((b) => b.name === editingRow.branch)?._id ||
                    branches.find((b) => b.name === editingRow.branch)?.id ||
                    "",
                }
              : {}
          }
          onSubmit={async (values) => {
            if (!editingRow) return;
            try {
              // Ensure both servicePrice and serviceprice are sent to backend for compatibility
              if (values.servicePrice && !values.serviceprice) {
                values.serviceprice = values.servicePrice;
              }
              await updateAppointment(editingRow.id, values);
              setRows((prev) =>
                prev.map((r: any) =>
                  String(r.id) === String(editingRow.id)
                    ? { ...r, ...values }
                    : r
                )
              );
              toast.success("Appointment updated successfully!", {
                position: "top-right",
              });
              setEditOpen(false);
              await fetchBranches();
            } catch (error) {
              console.error("Failed to update appointment:", error);
              toast.error("Failed to update appointment.", {
                position: "top-right",
              });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteAppointment(String(toDeleteId));
              setRows((prev) =>
                prev.filter((r: any) => String(r.id) !== String(toDeleteId))
              );
              toast.success("Appointment deleted successfully!", {
                position: "top-right",
              });
              await fetchBranches();
            } catch (error) {
              console.error("Failed to delete appointment:", error);
              toast.error("Failed to delete appointment.", {
                position: "top-right",
              });
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
        <GenericFormDialog
          open={dialogOpen}
          title="Add New Branch"
          onClose={() => setDialogOpen(false)}
          onSubmit={handleDialogSubmit}
          fields={fields}
          initialValues={{
            countryCode: "+961",
          }}
        />
        <PinAccessDialog
          open={pinDialogOpen}
          onClose={handleClosePinDialog}
          onVerified={handlePinVerified}
          title="Show finance"
          description="Enter the PIN to show finance. Use Send PIN if you need it."
        />

        {/* Invoice Edit Dialog */}
        <CustomDialog
          open={invoiceEditOpen}
          onClose={() => setInvoiceEditOpen(false)}
          title={`Edit Invoice: ${invoiceNumberLabel}`}
          paperSx={{ maxWidth: 800, width: "90vw" }}
        >
          <Box sx={{ p: 2 }}>
            {loadingInvoice ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Client info and date */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <CustomTextField
                    label="Client Name"
                    value={invoiceClientName}
                    fullWidth
                    size="small"
                    disabled
                  />
                  <CustomTextField
                    label="Email"
                    value={invoiceClientEmail}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <CustomTextField
                    label="Phone Number"
                    value={invoiceClientPhone}
                    fullWidth
                    size="small"
                    disabled
                  />
                  <CustomTextField
                    label="Date"
                    type="date"
                    value={invoiceDateValue}
                    fullWidth
                    size="small"
                    onChange={(event) =>
                      patchEditingInvoice({
                        invoice_date: event.target.value,
                      })
                    }
                  />
                </Box>

                {/* Status and Payment Method */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <CustomLabel text="Status" />
                    <Select
                      value={resolveStatusFormValue(editingInvoice)}
                      fullWidth
                      size="small"
                      onChange={(event) => {
                        const value = event.target.value as
                          | "paid"
                          | "unpaid"
                          | "pending";
                        patchEditingInvoice({
                          status: value,
                          status_id: statusStringToId(value),
                        });
                      }}
                    >
                      <MenuItem value="paid">paid</MenuItem>
                      <MenuItem value="unpaid">unpaid</MenuItem>
                      <MenuItem value="pending">pending</MenuItem>
                    </Select>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CustomLabel text="Payment method" />
                    <Select
                      value={resolvePaymentMethodFormValue(editingInvoice)}
                      fullWidth
                      size="small"
                      onChange={(event) => {
                        const value = event.target.value as
                          | "cash"
                          | "card"
                          | "bank transfer";
                        patchEditingInvoice({
                          payment_method_id: paymentMethodStringToId(value),
                        });
                      }}
                    >
                      <MenuItem value="cash">cash</MenuItem>
                      <MenuItem value="card">card</MenuItem>
                      <MenuItem value="bank transfer">bank transfer</MenuItem>
                    </Select>
                  </Box>
                </Box>

                {/* Notes */}
                <Box sx={{ mb: 2 }}>
                  <CustomLabel text="Notes" />
                  <CustomTextField
                    value={editingInvoice?.notes || ""}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    onChange={(event) =>
                      patchEditingInvoice({ notes: event.target.value })
                    }
                  />
                </Box>

                <InvoiceItemsEditor
                  value={invoiceItems}
                  onChange={(items) => setInvoiceItems(items)}
                  vatValue={vat}
                  onVatChange={(value) => setVat(value)}
                  discountValue={discount}
                  onDiscountChange={(value) => setDiscount(value)}
                  isEditMode
                  servicesList={invoiceServices}
                />

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <CustomButton
                    label="Cancel"
                    onClick={() => setInvoiceEditOpen(false)}
                    variant="text"
                    textColor={COLORS.MainColor}
                  />
                  <CustomButton
                    label="Submit"
                    onClick={async () => {
                      if (!editingInvoice) {
                        toast.error("No invoice data available.", {
                          position: "top-right",
                        });
                        return;
                      }
                      try {
                        const servicePayload = invoiceItems.map(
                          (item: any) => ({
                            service_id: resolveServiceIdForItem(item),
                            quantity: Number(item.quantity) || 0,
                            amount: formatAmountString(item.unitCost),
                            discount: formatAmountString(item.discount),
                            net_amount: formatAmountString(item.totalPrice),
                          })
                        );

                        const totalAmount = servicePayload.reduce(
                          (sum: number, item: any) =>
                            sum + Number(item.net_amount || 0),
                          0
                        );

                        const discountPercent = Number(discount) || 0;
                        const discountAmount =
                          Math.round(
                            ((totalAmount * discountPercent) / 100) * 100
                          ) / 100;
                        const amountAfterDiscount =
                          totalAmount - discountAmount;
                        const vatPercent = Number(vat) || 0;
                        const vatAmount =
                          Math.round(
                            ((amountAfterDiscount * vatPercent) / 100) * 100
                          ) / 100;
                        const finalAmount =
                          Math.round((amountAfterDiscount + vatAmount) * 100) /
                          100;

                        const resolvedStatusId =
                          editingInvoice?.status_id ||
                          statusStringToId(
                            resolveStatusFormValue(
                              editingInvoice?.status,
                              editingInvoice?.status_id
                            )
                          );

                        const resolvedPaymentMethodId =
                          editingInvoice?.payment_method_id ||
                          paymentMethodStringToId(
                            resolvePaymentMethodFormValue(editingInvoice)
                          );

                        const payload = {
                          client_id: editingInvoice.client_id,
                          appointment_id: editingInvoice.appointment_id,
                          business_id:
                            editingInvoice.business_id ||
                            editingInvoice.invoice?.business_id ||
                            editingInvoice.invoice?.businessId ||
                            editingInvoice.businessId,
                          status_id: resolvedStatusId,
                          invoice_number:
                            editingInvoice.invoice_number ||
                            editingInvoice.Invoice_number ||
                            editingInvoice.invoice?.invoice_number ||
                            editingInvoice.invoice?.Invoice_number,
                          total_amount: totalAmount,
                          discount: discountPercent,
                          payment_method_id: resolvedPaymentMethodId,
                          created_by:
                            editingInvoice.created_by || getUserId() || "1",
                          created_at:
                            editingInvoice.created_at ||
                            editingInvoice.invoice?.created_at ||
                            new Date().toISOString(),
                          Vat: vatPercent,
                          net_amount: formatAmountString(finalAmount),
                          services: servicePayload,
                          notes: editingInvoice?.notes || "",
                          invoice_date:
                            editingInvoice.invoice_date ||
                            editingInvoice.invoice?.invoice_date ||
                            editingInvoice.created_at?.split("T")?.[0],
                          id: editingInvoice.id,
                        };

                        await updateInvoice(editingInvoice.id, payload);
                        toast.success("Invoice updated successfully!", {
                          position: "top-right",
                        });
                        setInvoiceEditOpen(false);
                        setRevenueRefreshKey((prev) => prev + 1);
                      } catch (error) {
                        console.error("Failed to update invoice:", error);
                        toast.error("Failed to update invoice.", {
                          position: "top-right",
                        });
                      }
                    }}
                    variant="contained"
                    bgColor={COLORS.MainColor}
                  />
                </Box>
              </>
            )}
          </Box>
        </CustomDialog>
      </Box>
    </Box>
  );
};

export default Dashboard;
